import { ParseError } from '../../lib/errors.ts';
import type {
  ComicCard,
  ComicDetails,
  FilterOption,
  Page,
  PageRef,
  ReadGroup,
  SearchFiltersSchema,
  Tag,
} from '../types.ts';
import {
  GAMEBUS_SORT_OPTIONS,
  GAMEBUS_SOURCE_ID,
  GAMEBUS_TAG_FILTER_ID,
  GAMEBUS_WEB_BASE,
  type GameBusChapter,
  type GameBusTagOption,
} from './types.ts';

function parseError(message: string, value?: unknown): ParseError {
  return new ParseError(message, {
    source: GAMEBUS_SOURCE_ID,
    snippet: value === undefined ? undefined : JSON.stringify(value).slice(0, 500),
  });
}

function record(value: unknown, label: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw parseError(`GameBus ${label} must be an object`, value);
  }
  return value as Record<string, unknown>;
}

function envelopeData(value: unknown, label: string): unknown {
  const payload = record(value, `${label} envelope`);
  if (payload.code !== 200) {
    throw parseError(`GameBus ${label} response had non-OK code`, value);
  }
  return payload.data;
}

function nonEmptyString(value: unknown, label: string): string {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  throw parseError(`GameBus ${label} is missing`, value);
}

function optionalString(value: unknown): string | undefined {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return undefined;
}

function optionalNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

function optionalTimestamp(value: unknown): number | undefined {
  const raw = optionalString(value);
  if (!raw) return undefined;
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function statusTag(value: unknown): Tag | undefined {
  const status = optionalNumber(value);
  if (status === 1) return { id: 'status:ongoing', name: 'Ongoing', namespace: 'status' };
  if (status === 2) return { id: 'status:completed', name: 'Completed', namespace: 'status' };
  return undefined;
}

function tagFromString(name: string): Tag {
  return { id: name, name };
}

function tagsFromDetail(rawTags: unknown): Tag[] {
  if (!Array.isArray(rawTags)) return [];
  return rawTags.map((rawTag) => {
    const tag = record(rawTag, 'detail tag');
    return {
      id: nonEmptyString(tag.id, 'detail tag id'),
      name: nonEmptyString(tag.name, 'detail tag name'),
    };
  });
}

function tagsFromCard(rawTags: unknown): Tag[] | undefined {
  if (!Array.isArray(rawTags)) return undefined;
  const tags = rawTags.map((tag) => tagFromString(nonEmptyString(tag, 'card tag')));
  return tags.length > 0 ? tags : undefined;
}

function mapCard(rawCard: unknown): ComicCard {
  const card = record(rawCard, 'list comic');
  const out: ComicCard = {
    id: nonEmptyString(card.id, 'comic id'),
    source: GAMEBUS_SOURCE_ID,
    title: nonEmptyString(card.title, 'comic title'),
    coverUrl: nonEmptyString(card.cover_url, 'cover URL'),
  };
  const lastChapterIndex = optionalNumber(card.last_chapterIndex);
  if (lastChapterIndex !== undefined) out.pageCount = lastChapterIndex;
  const rating = optionalNumber(card.rating);
  if (rating !== undefined) out.rating = rating;
  const uploadedAt = optionalTimestamp(card.last_chapterAt) ?? optionalTimestamp(card.createdAt);
  if (uploadedAt !== undefined) out.uploadedAt = uploadedAt;
  const tags = tagsFromCard(card.tags);
  if (tags) out.tags = tags;
  return out;
}

export function parseGameBusPageList(value: unknown): Page<ComicCard> {
  const data = record(envelopeData(value, 'list'), 'list data');
  if (!Array.isArray(data.list)) throw parseError('GameBus list response is missing list', data);
  const page = optionalNumber(data.page) ?? 1;
  const pageSize = optionalNumber(data.pageSize) ?? data.list.length;
  const totalItems = optionalNumber(data.total);
  const totalPages =
    totalItems !== undefined && pageSize > 0 ? Math.ceil(totalItems / pageSize) : undefined;
  return {
    items: data.list.map(mapCard),
    page,
    hasMore: totalPages !== undefined ? page < totalPages : data.list.length >= pageSize,
    ...(totalPages !== undefined ? { totalPages } : {}),
    ...(totalItems !== undefined ? { totalItems } : {}),
  };
}

export function parseGameBusChapters(value: unknown): GameBusChapter[] {
  const data = record(envelopeData(value, 'chapters'), 'chapters data');
  if (!Array.isArray(data.list)) throw parseError('GameBus chapters response is missing list', data);
  return data.list
    .map((rawChapter): GameBusChapter => {
      const chapter = record(rawChapter, 'chapter');
      return {
        id: nonEmptyString(chapter.id, 'chapter id'),
        title: optionalString(chapter.title) ?? `Chapter ${nonEmptyString(chapter.id, 'chapter id')}`,
        orderIndex: optionalNumber(chapter.order_index) ?? 0,
        pageCount: optionalNumber(chapter.pages),
        createdAt: optionalTimestamp(chapter.createdAt),
      };
    })
    .sort((a, b) => a.orderIndex - b.orderIndex);
}

export function parseGameBusComic(value: unknown, chapters: GameBusChapter[]): ComicDetails {
  const comic = record(envelopeData(value, 'detail'), 'detail data');
  const id = nonEmptyString(comic.id, 'comic id');
  const author = optionalString(comic.author);
  const tags = tagsFromDetail(comic.tags);
  const status = statusTag(comic.status);
  if (status) tags.push(status);
  if (author) tags.push({ id: `author:${author}`, name: author, namespace: 'author' });
  const pageCount = chapters.reduce((sum, chapter) => sum + (chapter.pageCount ?? 0), 0);
  const details: ComicDetails = {
    id,
    source: GAMEBUS_SOURCE_ID,
    title: nonEmptyString(comic.title, 'comic title'),
    coverUrl: nonEmptyString(comic.cover_url, 'cover URL'),
    pageCount,
    tags,
    sourceUrl: `${GAMEBUS_WEB_BASE}/comic/${encodeURIComponent(id)}`,
  };
  const altTitle = optionalString(comic.subtitle);
  if (altTitle) details.altTitle = altTitle;
  const description = optionalString(comic.description);
  if (description) details.description = description;
  if (author) details.uploader = author;
  const rating = optionalNumber(comic.rating);
  if (rating !== undefined) details.rating = rating;
  const uploadedAt = optionalTimestamp(comic.last_chapterAt) ?? optionalTimestamp(comic.createdAt);
  if (uploadedAt !== undefined) details.uploadedAt = uploadedAt;
  if (chapters.length > 0) {
    details.readGroups = buildReadGroups(chapters, optionalString(comic.last_chapterId));
  }
  return details;
}

function buildReadGroups(chapters: GameBusChapter[], defaultChapterId?: string): ReadGroup[] {
  const fallbackDefault = defaultChapterId ?? chapters[0]?.id;
  return [
    {
      id: 'chapters',
      title: 'Chapters',
      kind: 'volume',
      units: chapters.map((chapter) => ({
        id: chapter.id,
        title: chapter.title,
        ...(chapter.pageCount !== undefined ? { pageCount: chapter.pageCount } : {}),
        ...(chapter.createdAt !== undefined ? { publishedAt: chapter.createdAt } : {}),
        sortKey: chapter.orderIndex,
        ...(chapter.id === fallbackDefault ? { isDefault: true } : {}),
      })),
    },
  ];
}

export function defaultChapterIdFromComic(value: unknown, chapters: GameBusChapter[]): string | undefined {
  const comic = record(envelopeData(value, 'detail'), 'detail data');
  const lastChapterId = optionalString(comic.last_chapterId);
  return lastChapterId ?? chapters[0]?.id;
}

export function parseGameBusPages(value: unknown): PageRef[] {
  const data = record(envelopeData(value, 'pages'), 'pages data');
  const chapter = record(data.chapter, 'pages chapter');
  const chapterId = nonEmptyString(chapter.id, 'pages chapter id');
  const comic = record(chapter.comic, 'pages comic');
  const comicId = nonEmptyString(comic.id, 'pages comic id');
  if (!Array.isArray(chapter.pages)) {
    throw parseError('GameBus pages response is missing pages', chapter);
  }
  return chapter.pages
    .map((rawPage) => {
      const page = record(rawPage, 'page');
      return {
        id: nonEmptyString(page.id, 'page id'),
        url: nonEmptyString(page.image_path, 'page image URL'),
        orderIndex: optionalNumber(page.order_index) ?? 0,
      };
    })
    .sort((a, b) => a.orderIndex - b.orderIndex)
    .map((page, index): PageRef => ({
      comicId,
      index,
      readUnitId: chapterId,
      readGroupId: 'chapters',
      resolvedUrl: page.url,
      extra: { gamebusPageId: page.id },
    }));
}

export function parseGameBusTags(value: unknown): GameBusTagOption[] {
  const data = envelopeData(value, 'tags');
  if (!Array.isArray(data)) throw parseError('GameBus tag catalog must be an array', data);
  return data.map((rawTag): GameBusTagOption => {
    const tag = record(rawTag, 'tag');
    const out: GameBusTagOption = {
      id: nonEmptyString(tag.id, 'tag id'),
      name: nonEmptyString(tag.name, 'tag name'),
    };
    const count = optionalNumber(tag.products);
    if (count !== undefined) out.count = count;
    return out;
  });
}

export function buildGameBusSearchFilters(tags: GameBusTagOption[]): SearchFiltersSchema {
  const tagOptions: FilterOption[] = tags.map((tag) => ({
    value: tag.id,
    label: tag.count !== undefined ? `${tag.name} (${tag.count})` : tag.name,
  }));
  return {
    fields: [
      {
        id: 'sort',
        type: 'select',
        label: 'Sort',
        options: [...GAMEBUS_SORT_OPTIONS],
        defaultValue: 'last_chapterAt',
      },
      {
        id: GAMEBUS_TAG_FILTER_ID,
        type: 'chips',
        label: 'Tag',
        options: tagOptions,
      },
    ],
  };
}
